import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { listJobs, type Job, type JobStatus } from "../api/jobs.ts";
import { listTechnicians } from "../api/technicians.ts";
import { listSites } from "../api/sites.ts";

const COLUMNS: { key: string; label: string; statuses: JobStatus[] }[] = [
  { key: "new",      label: "New Leads",           statuses: ["new"] },
  { key: "sent",     label: "Sent to Tech",         statuses: ["scheduled"] },
  { key: "approved", label: "Approved by Tech",     statuses: ["en_route", "on_site"] },
  { key: "done",     label: "Completed / Canceled", statuses: ["completed", "cancelled"] },
];

const STATUS_CHIPS: { status: JobStatus; label: string; active: string; inactive: string }[] = [
  { status: "new",       label: "New Job",           active: "bg-blue-600 text-white border-blue-600",       inactive: "border-blue-300 text-blue-700 hover:bg-blue-50" },
  { status: "scheduled", label: "Sent to Tech",      active: "bg-amber-500 text-white border-amber-500",     inactive: "border-amber-300 text-amber-700 hover:bg-amber-50" },
  { status: "en_route",  label: "Approved by Tech",  active: "bg-violet-600 text-white border-violet-600",   inactive: "border-violet-300 text-violet-700 hover:bg-violet-50" },
  { status: "on_site",   label: "Estimate Sent",     active: "bg-orange-500 text-white border-orange-500",   inactive: "border-orange-300 text-orange-700 hover:bg-orange-50" },
  { status: "completed", label: "Completed",         active: "bg-emerald-600 text-white border-emerald-600", inactive: "border-emerald-300 text-emerald-700 hover:bg-emerald-50" },
  { status: "cancelled", label: "Canceled",          active: "bg-slate-500 text-white border-slate-500",     inactive: "border-slate-300 text-slate-600 hover:bg-slate-50" },
];

const STATUS_BADGE: Record<JobStatus, string> = {
  new:       "bg-blue-100 text-blue-700",
  scheduled: "bg-amber-100 text-amber-700",
  en_route:  "bg-violet-100 text-violet-700",
  on_site:   "bg-orange-100 text-orange-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-500",
};

export default function JobsPage() {
  const { t } = useTranslation();
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<JobStatus | null>(null);
  const technicianFilter = sp.get("technician_id") ?? "";

  const { data, isLoading } = useQuery({
    queryKey: ["jobs", "kanban", technicianFilter, q],
    queryFn: () => listJobs({ technician_id: technicianFilter || undefined, q: q || undefined, limit: 500 }),
  });
  const { data: techs } = useQuery({ queryKey: ["technicians", "all"], queryFn: () => listTechnicians() });
  const { data: sites } = useQuery({ queryKey: ["sites", "all"], queryFn: listSites });

  const techMap = new Map((techs?.items ?? []).map(tech => [tech.user_id, tech.user_full_name ?? tech.user_email ?? "Tech"]));
  const siteMap = new Map((sites ?? []).map(s => [s.id, s.name]));

  const allItems = data?.items ?? [];
  const filtered = statusFilter ? allItems.filter(j => j.status === statusFilter) : allItems;

  const grouped: Record<JobStatus, Job[]> = {
    new: [], scheduled: [], en_route: [], on_site: [], completed: [], cancelled: [],
  };
  filtered.forEach((j) => grouped[j.status].push(j));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t("jobs.title")}</h1>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <select
            className="input w-full sm:max-w-xs"
            value={technicianFilter}
            onChange={(e) => {
              const v = e.target.value;
              const next = new URLSearchParams(sp);
              if (v) next.set("technician_id", v); else next.delete("technician_id");
              setSp(next);
            }}
          >
            <option value="">{t("jobs.allTechs")}</option>
            {techs?.items.map((tech) => (
              <option key={tech.id} value={tech.user_id}>{tech.user_full_name ?? tech.user_email ?? tech.user_id}</option>
            ))}
          </select>
          <input className="input w-full sm:max-w-xs" placeholder={t("common.search") ?? ""} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
            statusFilter === null
              ? "bg-slate-800 text-white border-slate-800"
              : "border-slate-300 text-slate-600 hover:bg-slate-50"
          }`}
          onClick={() => setStatusFilter(null)}
        >
          All
        </button>
        {STATUS_CHIPS.map(({ status, label, active, inactive }) => {
          const count = (data?.items ?? []).filter(j => j.status === status).length;
          const isActive = statusFilter === status;
          return (
            <button
              key={status}
              type="button"
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${isActive ? active : inactive}`}
              onClick={() => setStatusFilter(isActive ? null : status)}
            >
              {label}
              {count > 0 && (
                <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${isActive ? "bg-white/25 text-inherit" : "bg-current/10"}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {COLUMNS.map((col) => {
            const jobs = col.statuses.flatMap(s => grouped[s]);
            return (
              <div key={col.key} className="card p-3">
                <header className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold">{col.label}</h3>
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{jobs.length}</span>
                </header>
                <div className="space-y-2">
                  {jobs.length === 0 && (
                    <div className="rounded border border-dashed border-slate-200 p-3 text-center text-xs text-slate-400">—</div>
                  )}
                  {jobs.map((j) => (
                    <Link
                      key={j.id}
                      to={`/jobs/${j.id}`}
                      className="block rounded border border-slate-200 bg-white p-3 text-sm shadow-sm transition hover:border-brand-300 hover:shadow"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-mono text-xs text-slate-500">{j.job_number}</span>
                      </div>
                      <div className="mt-1 font-medium">{j.title}</div>
                      {j.scheduled_start && (
                        <div className="mt-1 text-xs text-slate-500">{new Date(j.scheduled_start).toLocaleString()}</div>
                      )}
                      {(j.address_line1 || j.city) && (
                        <div className="mt-1 text-xs text-slate-500">{[j.address_line1, j.city].filter(Boolean).join(", ")}</div>
                      )}
                      <div className="mt-1.5">
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${STATUS_BADGE[j.status] ?? "bg-slate-100 text-slate-500"}`}>
                          {t(`jobs.status.${j.status}`)}
                        </span>
                      </div>
                      {j.technician_id && techMap.has(j.technician_id) && (
                        <div className="mt-1.5 text-xs">
                          <span className="font-semibold text-slate-600">Tech: </span>
                          <span className="text-slate-700">{techMap.get(j.technician_id)}</span>
                        </div>
                      )}
                      {siteMap.has(j.site_id) && (
                        <div className="mt-0.5 text-xs">
                          <span className="font-semibold text-slate-400">Site: </span>
                          <span className="text-slate-400">{siteMap.get(j.site_id)}</span>
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
